import { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { API_BASE_URL } from "../constants";

// Types for the component - matching API structure
type PlayerData = {
  id: string;
  name: string;
  first_name: string | null;
  avatar: string | null;
  guesses: number;
  has_solved: boolean;
  has_given_up: boolean;
  solved_at: string | null;
  finished_timestamp: number | null;
  last_guess: string | null;
  revealed_positions: number[];
  revealed_chars: Record<string, number[]>;
  wrong_position: string[];
  not_used: string[];
  rank: number;
};

type GameOverviewData = {
  id: string;
  status: string;
  created: string;
  clue: string;
  answer_length: number;
  word_spans: [string, number, number][];
  image_url: string | null;
  players: PlayerData[];
  total_solved: number;
  total_given_up: number;
  letter_counts: Record<string, number>;
  special_chars_positions: [string, number][];
  config: Record<string, any>;
};

// Types for the component - matching API structure

// The actual PlayerLeaderboard component with isolated polling logic
const PlayerLeaderboard = ({
  gameId,
  onPlayerSolved,
}: {
  gameId: string;
  onPlayerSolved?: () => void;
}) => {
  const [players, setPlayers] = useState<PlayerData[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [retryCount, setRetryCount] = useState<number>(0);
  const maxRetries = 5;

  // Track the previous game data to detect new solves
  const prevGameDataRef = useRef<GameOverviewData | null>(null);
  // Track if we've already shown confetti for this session to prevent multiple confetti displays
  const hasShownConfettiRef = useRef<boolean>(false);

  // TEMPORARY: Mock data for testing
  const MOCK_MODE = false; // Set to false to disable mock data

  // Mock data to test all scenarios
  const getMockData = () => {
    const mockPlayers: PlayerData[] = [
      // Solved players with ties (group 1) - same guesses
      {
        id: "u1",
        name: "John Smith",
        first_name: "John",
        avatar: "https://api.dicebear.com/7.x/avataaars/svg?seed=John",
        guesses: 3,
        has_solved: true,
        has_given_up: false,
        solved_at: "2023-01-01T12:00:00Z",
        finished_timestamp: 1672574400,
        last_guess: "correct guess",
        revealed_positions: [0, 1, 2],
        revealed_chars: { a: [0], b: [1], c: [2] },
        wrong_position: [],
        not_used: [],
        rank: 1,
      },
      {
        id: "u2",
        name: "Jane Doe",
        first_name: "Jane",
        avatar: "https://api.dicebear.com/7.x/avataaars/svg?seed=Jane",
        guesses: 3,
        has_solved: true,
        has_given_up: false,
        solved_at: "2023-01-01T12:05:00Z",
        finished_timestamp: 1672574700,
        last_guess: "correct guess",
        revealed_positions: [0, 1, 2],
        revealed_chars: { a: [0], b: [1], c: [2] },
        wrong_position: [],
        not_used: [],
        rank: 1,
      },
      // Solved player (group 2)
      {
        id: "u3",
        name: "Bob Johnson",
        first_name: "Bob",
        avatar: "https://api.dicebear.com/7.x/avataaars/svg?seed=Bob",
        guesses: 5,
        has_solved: true,
        has_given_up: false,
        solved_at: "2023-01-01T12:10:00Z",
        finished_timestamp: 1672575000,
        last_guess: "correct guess",
        revealed_positions: [0, 1, 2],
        revealed_chars: { a: [0], b: [1], c: [2] },
        wrong_position: [],
        not_used: [],
        rank: 2,
      },
      // Solved players with ties (group 3) - same guesses
      {
        id: "u4",
        name: "Alice Williams",
        first_name: "Alice",
        avatar: "https://api.dicebear.com/7.x/avataaars/svg?seed=Alice",
        guesses: 7,
        has_solved: true,
        has_given_up: false,
        solved_at: "2023-01-01T12:15:00Z",
        finished_timestamp: 1672575300,
        last_guess: "correct guess",
        revealed_positions: [0, 1, 2],
        revealed_chars: { a: [0], b: [1], c: [2] },
        wrong_position: [],
        not_used: [],
        rank: 3,
      },
      {
        id: "u5",
        name: "Charlie Brown",
        first_name: "Charlie",
        avatar: "https://api.dicebear.com/7.x/avataaars/svg?seed=Charlie",
        guesses: 7,
        has_solved: true,
        has_given_up: false,
        solved_at: "2023-01-01T12:20:00Z",
        finished_timestamp: 1672575600,
        last_guess: "correct guess",
        revealed_positions: [0, 1, 2],
        revealed_chars: { a: [0], b: [1], c: [2] },
        wrong_position: [],
        not_used: [],
        rank: 3,
      },
      {
        id: "u6",
        name: "Diana Miller",
        first_name: "Diana",
        avatar: null,
        guesses: 7,
        has_solved: true,
        has_given_up: false,
        solved_at: "2023-01-01T12:25:00Z",
        finished_timestamp: 1672575900,
        last_guess: "correct guess",
        revealed_positions: [0, 1, 2],
        revealed_chars: { a: [0], b: [1], c: [2] },
        wrong_position: [],
        not_used: [],
        rank: 3,
      },
      // In progress players
      {
        id: "u7",
        name: "Edward Jones",
        first_name: "Edward",
        avatar: "https://api.dicebear.com/7.x/avataaars/svg?seed=Edward",
        guesses: 2,
        has_solved: false,
        has_given_up: false,
        solved_at: null,
        finished_timestamp: null,
        last_guess: "wrong guess",
        revealed_positions: [0],
        revealed_chars: { a: [0] },
        wrong_position: ["b"],
        not_used: ["c"],
        rank: 0,
      },
      {
        id: "u8",
        name: "Fiona Garcia",
        first_name: "Fiona",
        avatar: null,
        guesses: 4,
        has_solved: false,
        has_given_up: false,
        solved_at: null,
        finished_timestamp: null,
        last_guess: "wrong guess",
        revealed_positions: [1],
        revealed_chars: { b: [1] },
        wrong_position: ["a"],
        not_used: ["c"],
        rank: 0,
      },
      // Gave up players
      {
        id: "u9",
        name: "George Wilson",
        first_name: "George",
        avatar: "https://api.dicebear.com/7.x/avataaars/svg?seed=George",
        guesses: 8,
        has_solved: false,
        has_given_up: true,
        solved_at: null,
        finished_timestamp: 1672576200,
        last_guess: "gave up",
        revealed_positions: [2],
        revealed_chars: { c: [2] },
        wrong_position: [],
        not_used: ["a", "b"],
        rank: 0,
      },
      {
        id: "u10",
        name: "Hannah Martinez",
        first_name: "Hannah",
        avatar: null,
        guesses: 6,
        has_solved: false,
        has_given_up: true,
        solved_at: null,
        finished_timestamp: 1672576500,
        last_guess: "gave up",
        revealed_positions: [],
        revealed_chars: {},
        wrong_position: ["a", "b", "c"],
        not_used: [],
        rank: 0,
      },
    ];

    return {
      id: "mock-game-id",
      status: "active",
      created: "2023-01-01T00:00:00Z",
      clue: "Mock Game Clue",
      answer_length: 3,
      word_spans: [["word", 0, 3] as [string, number, number]],
      image_url: "https://picsum.photos/400/300",
      players: mockPlayers,
      total_solved: 6,
      total_given_up: 2,
      letter_counts: { a: 1, b: 1, c: 1 },
      special_chars_positions: [],
      config: {},
    };
  };

  // Fetch game data with exponential backoff retry logic
  const fetchGameData = useCallback(async () => {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/v2/games/live-overview/${gameId}`,
      );

      // Check for newly solved puzzles to trigger confetti
      if (prevGameDataRef.current && response.data) {
        const prevSolvedCount = prevGameDataRef.current.total_solved || 0;
        const newSolvedCount = response.data.total_solved || 0;

        // Find who just solved - compare previous players with current players
        if (newSolvedCount > prevSolvedCount && !hasShownConfettiRef.current) {
          // Someone new has solved the puzzle!
          console.log(
            `Puzzle solved! Previous solved: ${prevSolvedCount}, New solved: ${newSolvedCount}`,
          );

          // Call the callback from parent to trigger confetti
          if (onPlayerSolved) {
            onPlayerSolved();
          }

          // Mark that we've detected a solve for this session
          hasShownConfettiRef.current = true;

          // Log who solved it (optional)
          const previouslySolved = prevGameDataRef.current.players
            .filter((p: PlayerData) => p.has_solved)
            .map((p: PlayerData) => p.id);
          const newlySolved = response.data.players.filter(
            (p: PlayerData) => p.has_solved && !previouslySolved.includes(p.id),
          );

          if (newlySolved.length > 0) {
            console.log(
              "New players who solved the puzzle:",
              newlySolved.map((p: PlayerData) => p.name || p.first_name),
            );
          }
        }

        // If someone previously solved but no longer has (e.g., game reset), allow confetti again
        if (newSolvedCount === 0 && prevSolvedCount > 0) {
          hasShownConfettiRef.current = false;
        }
      }

      // Use mock data if MOCK_MODE is enabled
      if (MOCK_MODE) {
        const mockData = getMockData();
        prevGameDataRef.current = mockData;
        setPlayers(mockData.players);
        setLoading(false);
        setError(null);
        setRetryCount(0);
      } else {
        prevGameDataRef.current = response.data;
        setPlayers(response.data.players);
        setLoading(false);
        setError(null);
        setRetryCount(0);
      }
    } catch (err) {
      console.error("Error fetching game data:", err);
      setError("Failed to load player data. Retrying...");

      // Implement exponential backoff
      if (retryCount < maxRetries) {
        const backoffDelay = Math.min(1000 * Math.pow(2, retryCount), 10000);
        setTimeout(() => {
          setRetryCount((prev) => prev + 1);
          fetchGameData();
        }, backoffDelay);
      } else {
        setError("Failed to load player data. Please try refreshing.");
      }
    }
  }, [gameId, retryCount]);

  // Removed temporary mock data

  // Set up polling
  useEffect(() => {
    // Define a clean polling function that reuses our main fetchGameData
    const pollGameData = async () => {
      try {
        await fetchGameData();
      } catch (err) {
        console.error("Error in polling interval:", err);
      }
    };

    if (gameId) {
      // Initial fetch
      pollGameData();

      // Set up polling interval (every 3-5 seconds)
      const intervalId = setInterval(pollGameData, 4000);

      // Handle component mount/unmount
      return () => {
        // Clean up interval on unmount
        clearInterval(intervalId);
      };
    }
  }, [gameId, fetchGameData]);

  useEffect(() => {
    // Reset the confetti flag when the component is mounted
    hasShownConfettiRef.current = false;

    return () => {
      // Clean up any resources on unmount
    };
  }, []);

  // Separate players into categories
  const solvedPlayers = players.filter((p: PlayerData) => p.has_solved);
  const playingPlayers = players.filter(
    (p: PlayerData) => !p.has_solved && !p.has_given_up,
  );
  const gaveUpPlayers = players.filter((p: PlayerData) => p.has_given_up);

  // Sort solved players by guesses (ascending - fewer guesses is better)
  solvedPlayers.sort((a: PlayerData, b: PlayerData) => a.guesses - b.guesses);

  // Group solved players by rank and guesses for ties
  const groupedSolvedPlayers = solvedPlayers.reduce<
    Record<string, PlayerData[]>
  >((groups, player) => {
    const key = player.guesses.toString();
    if (!groups[key]) groups[key] = [];
    groups[key].push(player);
    return groups;
  }, {});

  // Sort groups by guesses (lowest first)
  const sortedSolvedGroups = Object.values(groupedSolvedPlayers).sort(
    (a: PlayerData[], b: PlayerData[]) => a[0].guesses - b[0].guesses,
  );

  // Sort playing players by the number of guesses made, descending, but don't group them (individual cards per player)
  const sortedPlayingPlayers = playingPlayers.sort(
    (a: PlayerData, b: PlayerData) => b.guesses - a.guesses,
  );

  // Sort players who gave up by the number of guesses made before giving up, descending, but don't group them (individual cards per player)
  const sortedGaveUpPlayers = gaveUpPlayers.sort(
    (a: PlayerData, b: PlayerData) => b.guesses - a.guesses,
  );

  // Loading state
  if (loading) {
    return (
      <div className="flex items-center justify-center p-8 bg-white/10 backdrop-blur-md rounded-xl shadow-2xl min-h-[300px]">
        <motion.div
          className="w-12 h-12 border-t-3 border-purple-300 border-solid rounded-full"
          animate={{ rotate: 360 }}
          transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
        ></motion.div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="p-8 bg-red-900/50 backdrop-blur-md rounded-xl shadow-2xl text-red-100">
        <h3 className="font-semibold text-xl mb-3">
          Error Loading Leaderboard
        </h3>
        <p className="text-base">{error}</p>
      </div>
    );
  }

  return (
    <>
      {/* Enhanced Leaderboard container styling */}
      <div className="leaderboard-container bg-brandLight backdrop-blur-lg rounded-xl shadow-2xl p-6 md:p-8 border-2 border-brand/50 w-full text-indigo-900">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-semibold">Leaderboard</h2>

          {/* Live indicator */}
          <div className="flex items-center gap-3">
            <div className="relative">
              <motion.div
                className="w-3 h-3 rounded-full bg-green-500"
                animate={{
                  scale: [1, 1.2, 1],
                  opacity: [1, 0.8, 1],
                }}
                transition={{
                  duration: 2,
                  repeat: Infinity,
                  ease: "easeInOut",
                }}
              />
              <motion.div
                className="absolute inset-0 w-3 h-3 rounded-full bg-green-300"
                animate={{
                  scale: [1, 2],
                  opacity: [0.5, 0],
                }}
                transition={{
                  duration: 2,
                  repeat: Infinity,
                  ease: "easeOut",
                }}
              />
            </div>
            <span className="text-sm text-gray-800">Live Updates</span>
          </div>
        </div>

        {loading && players.length === 0 ? (
          <div className="flex items-center justify-center p-6 min-h-[100px]">
            <motion.div
              className="w-8 h-8 border-t-2 border-purple-300 border-solid rounded-full"
              animate={{ rotate: 360 }}
              transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
            ></motion.div>
          </div>
        ) : !loading && players.length === 0 ? (
          <p className="text-center text-indigo-900 font-semibold text-lg">
            No players yet
          </p>
        ) : (
          <div className="space-y-6">
            {/* Solved Players Section */}
            <motion.div
              className="space-y-3"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5 }}
            >
              {sortedSolvedGroups.length > 0 && (
                <h3 className="text-lg font-semibold text-indigo-900 my-6">
                  <span className="text-xl pr-2">🏆 Solved</span>
                </h3>
              )}

              {sortedSolvedGroups.map((group, groupIndex) => (
                <motion.div
                  key={group.map((p) => p.id).join("-")}
                  className={`player-card relative flex items-center p-4 bg-white/5 hover:bg-white/10 rounded-xl shadow-lg transition-all duration-300 ${group[0].has_solved ? "border-l-4 border-green-500" : "border-l-4 border-transparent"}`}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: groupIndex * 0.05 }}
                >
                  {/* Player Avatars and Names */}
                  <div className="flex-grow px-4">
                    <div className="flex items-center flex-wrap">
                      {group.map((player) => (
                        <div key={player.id} className="flex items-center">
                          <div className="flex items-center gap-2 m-1">
                            {player.avatar ? (
                              <div className="avatar-container relative transition-all duration-300 hover:scale-110">
                                <img
                                  src={player.avatar}
                                  alt={player.name}
                                  className={`rounded-full border-2 border-green-400/60 shadow-md object-cover ${group.length > 1 ? "w-10 h-10" : "w-12 h-12"}`}
                                />
                              </div>
                            ) : (
                              <div className="avatar-container relative transition-all duration-300 hover:scale-110">
                                <div
                                  className={`rounded-full bg-gradient-to-br from-green-400 to-green-600 text-white flex items-center justify-center font-bold border-2 border-green-300 shadow-md ${group.length > 1 ? "w-10 h-10 text-lg" : "w-12 h-12 text-xl"}`}
                                >
                                  {player.first_name?.charAt(0) ||
                                    player.name?.charAt(0)}
                                </div>
                              </div>
                            )}
                            <span
                              className={`font-medium text-gray-900 ${group.length > 1 ? "text-base" : "text-lg"}`}
                            >
                              {player.first_name || player.name}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Guesses with medals */}
                  <div className="flex-shrink-0 text-right flex items-center gap-2">
                    <span className="text-xl font-bold text-brand">
                      {group[0].guesses}{" "}
                      <span className="text-sm text-brand/80">
                        guess{group[0].guesses !== 1 ? "es" : ""}
                      </span>
                    </span>

                    {/* Medal indicator for top players */}
                    {group[0].rank > 0 && group[0].rank <= 3 && (
                      <motion.span
                        className="text-xl"
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        transition={{
                          type: "spring",
                          stiffness: 300,
                          damping: 15,
                        }}
                      >
                        {group[0].rank === 1
                          ? "🥇"
                          : group[0].rank === 2
                            ? "🥈"
                            : "🥉"}
                      </motion.span>
                    )}
                  </div>
                </motion.div>
              ))}

              {/* Playing Players Section */}
              {sortedPlayingPlayers.length > 0 && (
                <div className="mt-6">
                  <h3 className="text-lg font-semibold text-indigo-900 my-6">
                    <span className="text-yellow-500 text-xl pr-2">🤔</span>
                    Still Playing
                  </h3>
                  <motion.div
                    className="space-y-3"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.5, delay: 0.2 }}
                  >
                    {sortedPlayingPlayers.map(
                      (player: PlayerData, playerIndex: number) => (
                        <motion.div
                          key={player.id}
                          className="player-card relative flex items-center p-4 bg-white/5 hover:bg-white/10 rounded-xl shadow-lg transition-all duration-300 border-l-4 border-yellow-500"
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: playerIndex * 0.05 }}
                        >
                          {/* Player Name and Avatar */}
                          <div className="flex-grow px-4">
                            <div className="flex items-center gap-2">
                              {player.avatar ? (
                                <div className="avatar-container relative transition-all duration-300 hover:scale-110">
                                  <img
                                    src={player.avatar}
                                    alt={player.name}
                                    className="w-12 h-12 rounded-full border-2 border-brand/60 shadow-md object-cover"
                                  />
                                  {player.rank > 0 && player.rank <= 3 && (
                                    <div className="absolute -top-2 -right-2 text-lg">
                                      {player.rank === 1
                                        ? "🥇"
                                        : player.rank === 2
                                          ? "🥈"
                                          : "🥉"}
                                    </div>
                                  )}
                                </div>
                              ) : (
                                <div className="avatar-container relative transition-all duration-300 hover:scale-110">
                                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 text-white flex items-center justify-center text-xl font-bold border-2 border-indigo-300 shadow-md">
                                    {player.first_name?.charAt(0) ||
                                      player.name?.charAt(0)}
                                  </div>
                                  {player.rank > 0 && player.rank <= 3 && (
                                    <div className="absolute -top-2 -right-2 text-lg">
                                      {player.rank === 1
                                        ? "🥇"
                                        : player.rank === 2
                                          ? "🥈"
                                          : "🥉"}
                                    </div>
                                  )}
                                </div>
                              )}
                              <span className="text-lg font-medium text-gray-900">
                                {player.first_name || player.name}
                              </span>
                            </div>
                          </div>

                          {/* Guesses */}
                          <div className="flex-shrink-0 text-right">
                            <span className="text-xl font-bold text-brand">
                              {player.guesses}{" "}
                              <span className="text-sm text-brand/80">
                                guess{player.guesses !== 1 ? "es" : ""}
                              </span>
                            </span>
                          </div>
                        </motion.div>
                      ),
                    )}
                  </motion.div>
                </div>
              )}

              {/* Gave Up Section */}
              {sortedGaveUpPlayers.length > 0 && (
                <div className="mt-6">
                  <h3 className="text-lg font-semibold text-indigo-900 my-6">
                    <span className="text-red-500 text-xl pr-2">💝</span>
                    Valiant Attempts
                  </h3>
                  <motion.div
                    className="space-y-3"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.5, delay: 0.3 }}
                  >
                    {sortedGaveUpPlayers.map(
                      (player: PlayerData, playerIndex: number) => (
                        <motion.div
                          key={player.id}
                          className="player-card relative flex items-center p-4 bg-white/5 hover:bg-white/10 rounded-xl shadow-lg transition-all duration-300 border-l-4 border-red-500/50"
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: playerIndex * 0.05 }}
                        >
                          {/* Player Name and Avatar */}
                          <div className="flex-grow px-4">
                            <div className="flex items-center gap-2">
                              {player.avatar ? (
                                <div className="avatar-container relative transition-all duration-300 hover:scale-110">
                                  <img
                                    src={player.avatar}
                                    alt={player.name}
                                    className="w-12 h-12 rounded-full border-2 border-red-400/60 shadow-md object-cover"
                                  />
                                </div>
                              ) : (
                                <div className="avatar-container relative transition-all duration-300 hover:scale-110">
                                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-red-400 to-pink-600 text-white flex items-center justify-center text-xl font-bold border-2 border-red-300 shadow-md">
                                    {player.first_name?.charAt(0) ||
                                      player.name?.charAt(0)}
                                  </div>
                                </div>
                              )}
                              <span className="text-lg font-medium text-gray-900">
                                {player.first_name || player.name}
                              </span>
                            </div>
                          </div>

                          {/* Guesses */}
                          <div className="flex-shrink-0 text-right">
                            <span className="text-sm font-bold text-brand/80">
                              <span className="text-xl text-brand">
                                {player.guesses}{" "}
                              </span>
                              <span className="text-sm text-brand/80">
                                guess{player.guesses !== 1 ? "es" : ""}
                              </span>
                            </span>
                          </div>
                        </motion.div>
                      ),
                    )}
                  </motion.div>
                </div>
              )}
            </motion.div>
          </div>
        )}
      </div>
    </>
  );
};

export default PlayerLeaderboard;
